import LoadingPage from 'gcs-common/components/LoadingPage/LoadingPage';
import NotificationPage from 'gcs-common/components/NotificationPage/NotificationPage';
import AuthProtectedRoute from 'gcs-common/components/protected-routes/auth-protected-route';
import NoDetails from 'gcs-common/components/Tickets/TicketChat/NoDetails/NoDetails';
import TicketChat from 'gcs-common/components/Tickets/TicketChat/TicketChat';
import TicketDetails from 'gcs-common/components/Tickets/TicketDetails/TicketDetails';
import TicketForwardMessage from 'gcs-common/components/Tickets/TicketForwardMessage/TicketForwardMessage';
import TicketNew from 'gcs-common/components/Tickets/TicketNew/TicketNew';
import {
  ADMINISTRATION_ROUTES,
  CHAT_PATHS,
  CHAT_ROUTES,
  CODE_ROUTES,
  ETS_ROUTES,
  LOGIN_ROUTES,
  REGISTRATION_ROUTES,
} from 'gcs-common/constants/routes';
import { Suspense } from 'react';
import { createBrowserRouter, Navigate, type useLocation } from 'react-router-dom';
import AdministrationWrapper from '../components/AdministrationWrapper/AdministrationWrapper';
import AgentDetail from '../components/AdministrationWrapper/Agent/AgentDetail/AgentDetail';
import AgentOverview from '../components/AdministrationWrapper/Agent/AgentOverview/AgentOverview';
import BusinessUnitDetail from '../components/AdministrationWrapper/BusinessUnits/BusinessUnitDetail/BusinessUnitDetail';
import BusinessUnitOverview from '../components/AdministrationWrapper/BusinessUnits/BusinessUnitOverview/BusinessUnitOverview';
import ChannelDetail from '../components/AdministrationWrapper/Channels/ChannelDetail/ChannelDetail';
import CustomerResponsibilityMembersOverview from '../components/AdministrationWrapper/CustomerResponsibilityMembers/CustomerResponsibilityMembersOverview/CustomerResponsibilityMembersOverview';
import ErpCustomerDetail from '../components/AdministrationWrapper/ErpCustomer/ErpCustomerDetail/ErpCustomerDetail';
import ErpCustomerOverview from '../components/AdministrationWrapper/ErpCustomer/ErpCustomerOverview/ErpCustomerOverview';
import FeatureFlagsOverview from '../components/AdministrationWrapper/FeatureFlags/FeatureFlagsOverview';
import HouseDetail from '../components/AdministrationWrapper/House/HouseDetail/HouseDetail';
import HouseOverview from '../components/AdministrationWrapper/House/HouseOverview/HouseOverview';
import TeamDetail from '../components/AdministrationWrapper/Teams/TeamDetail/TeamDetail';
import TeamOverview from '../components/AdministrationWrapper/Teams/TeamOverview/TeamOverview';
import TenantDetail from '../components/AdministrationWrapper/Tenants/TenantDetail/TenantDetail';
import TenantOverview from '../components/AdministrationWrapper/Tenants/TenantOverview/TenantOverview';
import App from '../components/App/App';
import ChatRoot from '../components/ChatRoot/ChatRoot';
import ChatWrapper from '../components/ChatWrapper/ChatWrapper';
import DeleteAccountPage from '../components/DeleteAccountPage/DeleteAccountPage';
import ErrorBoundaryFallback from '../components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import Imprint from '../components/Imprint/Imprint';
import LandingRoot from '../components/LandingRoot/LandingRoot';
import LoggedInRoot from '../components/LoggedInRoot/LoggedInRoot';
import OnlinePlusLoginWeb from '../components/OnlinePlus/OnlinePlusLoginWeb/OnlinePlusLoginWeb';
import OnlinePlusProfileFormWeb from '../components/OnlinePlus/OnlinePlusProfileFormWeb/OnlinePlusProfileFormWeb';
import AdministrationProtectedRoute from '../components/ProtectedRoutes/AdministrationProtectedRoute';
import ApiProtectedRoute from '../components/ProtectedRoutes/ApiProtectedRoute';
import LoggedInProtectedRoute from '../components/ProtectedRoutes/LoggedInProtectedRoute';
import PermissionProtectedRoute from '../components/ProtectedRoutes/PermissionProtectedRoute';
import RegisteredUserProtectedRoute from '../components/ProtectedRoutes/RegisteredUserProtectedRoute';
import TermsAndPrivacy from '../components/TermsAndPrivacy/TermsAndPrivacy';
import TicketDashboard from '../components/Tickets/TicketDashboard/TicketDashboard';
import RequestUserCodeWeb from '../components/UserCodes/RequestUserCodeWeb/RequestUserCodeWeb';
import SubmitUserCodeWeb from '../components/UserCodes/SubmitUserCodeWeb/SubmitUserCodeWeb';

export interface RegistrationLocationState {
  sourceLocation?: ReturnType<typeof useLocation>;
}

export const routes = [
  {
    path: '/',
    errorElement: <ErrorBoundaryFallback />,
    element: <App />,
    children: [
      {
        index: true,
        element: <Navigate to={CHAT_ROUTES.CHAT} replace />,
      },
      {
        element: <AuthProtectedRoute />,
        children: [
          {
            element: (
              <LoggedInProtectedRoute>
                <ApiProtectedRoute />
              </LoggedInProtectedRoute>
            ),
            children: [
              {
                element: (
                  <RegisteredUserProtectedRoute>
                    <LoggedInRoot />
                  </RegisteredUserProtectedRoute>
                ),
                children: [
                  {
                    path: CHAT_ROUTES.CHAT,
                    element: <ChatRoot />,
                    children: [
                      {
                        path: CHAT_ROUTES.TICKETS,
                        element: <TicketChat />,
                        children: [
                          {
                            index: true,
                            element: <NoDetails />,
                          },
                          {
                            path: ':routeTicketId',
                            element: <TicketDetails />,
                          },
                          {
                            path: ':routeTicketId/forward/:type/:index',
                            element: <TicketForwardMessage />,
                          },
                          {
                            path: CHAT_PATHS.NEW_TICKET,
                            element: <TicketNew />,
                          },
                        ],
                      },
                      {
                        path: ':routeChannelId',
                        element: <ChatWrapper />,
                      },
                    ],
                  },
                  {
                    path: ETS_ROUTES.DASHBOARD,
                    element: <TicketDashboard />,
                  },
                  {
                    path: ADMINISTRATION_ROUTES.ADMINISTRATION,
                    element: (
                      <AdministrationProtectedRoute>
                        <Suspense fallback={<LoadingPage />}>
                          <AdministrationWrapper />
                        </Suspense>
                      </AdministrationProtectedRoute>
                    ),
                    children: [
                      {
                        path: 'erpCustomers/channel/:id',
                        element: <ChannelDetail />,
                      },
                      {
                        path: 'erpCustomers/:id',
                        element: <ErpCustomerDetail />,
                      },
                      {
                        path: 'erpCustomers',
                        element: <ErpCustomerOverview />,
                      },
                      {
                        path: 'teams/:id',
                        element: <TeamDetail />,
                      },
                      {
                        path: 'teams',
                        element: <TeamOverview />,
                      },
                      {
                        path: 'users/channel/:id',
                        element: <ChannelDetail />,
                      },
                      {
                        path: 'users/:id',
                        element: <AgentDetail />,
                      },
                      {
                        path: 'users',
                        element: <AgentOverview />,
                      },
                      {
                        path: 'houses/:id',
                        element: <HouseDetail />,
                      },
                      {
                        path: 'houses',
                        element: <HouseOverview />,
                      },
                      {
                        path: 'tenants/:id',
                        element: (
                          <PermissionProtectedRoute permission={['global_admin']}>
                            <TenantDetail />
                          </PermissionProtectedRoute>
                        ),
                      },
                      {
                        path: 'tenants',
                        element: (
                          <PermissionProtectedRoute permission={['global_admin']}>
                            <TenantOverview />
                          </PermissionProtectedRoute>
                        ),
                      },
                      {
                        path: 'business_units/:id',
                        element: (
                          <PermissionProtectedRoute permission={['global_admin']}>
                            <BusinessUnitDetail />
                          </PermissionProtectedRoute>
                        ),
                      },
                      {
                        path: 'business_units',
                        element: (
                          <PermissionProtectedRoute permission={['global_admin']}>
                            <BusinessUnitOverview />
                          </PermissionProtectedRoute>
                        ),
                      },
                      {
                        path: 'responsibilities',
                        element: (
                          <PermissionProtectedRoute permission={['administration', 'global_admin']}>
                            <CustomerResponsibilityMembersOverview />
                          </PermissionProtectedRoute>
                        ),
                      },
                      {
                        path: 'feature-flags',
                        element: (
                          <PermissionProtectedRoute permission={['global_admin']}>
                            <FeatureFlagsOverview />
                          </PermissionProtectedRoute>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: REGISTRATION_ROUTES.REGISTRATION_PROFILE,
                element: <OnlinePlusProfileFormWeb />,
              },
              {
                path: REGISTRATION_ROUTES.REGISTRATION_EXTERNAL,
                element: <OnlinePlusLoginWeb />,
              },
              {
                path: REGISTRATION_ROUTES.REGISTRATION_NOTIFICATIONS,
                element: <NotificationPage />,
              },
            ],
          },
          {
            path: LOGIN_ROUTES.LOGIN,
            element: <LandingRoot />,
          },
          {
            path: CODE_ROUTES.REQUEST_CODE,
            element: <RequestUserCodeWeb />,
          },
          {
            path: CODE_ROUTES.SUBMIT_CODE,
            element: <SubmitUserCodeWeb />,
          },
        ],
      },
      {
        path: LOGIN_ROUTES.LOGIN_TERMS,
        element: <TermsAndPrivacy withTerms withPrivacy withOptOut />,
      },
      {
        path: LOGIN_ROUTES.LOGIN_IMPRINT,
        element: <Imprint />,
      },
      {
        path: LOGIN_ROUTES.LOGIN_DELETE_ACCOUNT,
        element: <DeleteAccountPage />,
      },
    ],
  },
  // redirect all nonexisting routes to root
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];

const router = createBrowserRouter(routes);
export default router;
